import { useState, useEffect, useCallback } from 'react';

/** @import { TooltipPosition } from '../DashboardNewTypes' */

/**
 * @typedef {Object} UseTooltipsReturn
 * @property {string | null} openTooltipId
 * @property {TooltipPosition | null} popupPosition
 * @property {string | null} openMaturityTooltipId
 * @property {TooltipPosition | null} maturityPopupPosition
 * @property {string | null} openFundingTooltipId
 * @property {TooltipPosition | null} fundingPopupPosition
 * @property {string | null} openRateChangeTooltipId
 * @property {TooltipPosition | null} rateChangePopupPosition
 * @property {boolean} showIsaTooltip
 * @property {(event: React.MouseEvent, accountId: string) => void} handleInfoIconClick
 * @property {(event: React.MouseEvent, accountId: string) => void} handleMaturityInfoIconClick
 * @property {(event: React.MouseEvent, accountId: string) => void} handleAvailableBalanceIconClick
 * @property {(event: React.MouseEvent, accountId: string) => void} handleFundingInfoIconClick
 * @property {(event: React.MouseEvent, accountId: string) => void} handleRateChangeInfoIconClick
 * @property {() => void} toggleIsaTooltip
 */

/**
 * Custom hook to manage tooltip state and click-outside behavior
 * @returns {UseTooltipsReturn} Tooltip state and handlers
 */
export function useTooltips() {
    const [openTooltipId, setOpenTooltipId] = useState(/** @type {string | null} */ (null));
    const [popupPosition, setPopupPosition] = useState(/** @type {TooltipPosition | null} */ (null));
    const [openMaturityTooltipId, setOpenMaturityTooltipId] = useState(/** @type {string | null} */ (null));
    const [maturityPopupPosition, setMaturityPopupPosition] = useState(/** @type {TooltipPosition | null} */ (null));
    const [openFundingTooltipId, setOpenFundingTooltipId] = useState(/** @type {string | null} */ (null));
    const [fundingPopupPosition, setFundingPopupPosition] = useState(/** @type {TooltipPosition | null} */ (null));
    const [openRateChangeTooltipId, setOpenRateChangeTooltipId] = useState(/** @type {string | null} */ (null));
    const [rateChangePopupPosition, setRateChangePopupPosition] = useState(/** @type {TooltipPosition | null} */ (null));
    const [showIsaTooltip, setShowIsaTooltip] = useState(false);

    // Handle click outside to close tooltips
    useEffect(() => {
        const handleClickOutside = (/** @type {MouseEvent} */ event) => {
            const target = /** @type {Element | null} */ (event.target);
            
            if (openTooltipId && target && 
                !target.closest('.interest-rate-tooltip-wrapper') && 
                !target.closest('.interest-rate-tooltip') &&
                !target.closest('.interest-rate-tooltip-inline')) {
                setOpenTooltipId(null);
                setPopupPosition(null);
            }
            
            if (openMaturityTooltipId && target && 
                !target.closest('.maturity-info-clickable') && 
                !target.closest('.maturity-info-tooltip') && 
                !target.closest('.maturity-info-tooltip-inline') &&
                !target.closest('.account-card__info-icon')) {
                setOpenMaturityTooltipId(null);
                setMaturityPopupPosition(null);
            }
            
            if (openFundingTooltipId && target && 
                !target.closest('.funding-info-clickable') && 
                !target.closest('.funding-info-tooltip') && 
                !target.closest('.funding-info-tooltip-inline') &&
                !target.closest('.account-card__info-icon')) {
                setOpenFundingTooltipId(null);
                setFundingPopupPosition(null);
            }
            
            if (openRateChangeTooltipId && target && 
                !target.closest('.rate-change-info-clickable') && 
                !target.closest('.rate-change-info-tooltip') && 
                !target.closest('.rate-change-info-tooltip-inline') &&
                !target.closest('.account-card__info-icon')) {
                setOpenRateChangeTooltipId(null);
                setRateChangePopupPosition(null);
            }
            
            if (showIsaTooltip && target && 
                !target.closest('.isa-allowance__info-icon') && 
                !target.closest('.isa-allowance__info-popup')) {
                setShowIsaTooltip(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openTooltipId, openMaturityTooltipId, openFundingTooltipId, openRateChangeTooltipId, showIsaTooltip]);

    // Handle scroll to close tooltips
    useEffect(() => {
        const handleScroll = () => {
            if (openTooltipId) {
                setOpenTooltipId(null);
                setPopupPosition(null);
            }
            if (openMaturityTooltipId) {
                setOpenMaturityTooltipId(null);
                setMaturityPopupPosition(null);
            }
            if (openFundingTooltipId) {
                setOpenFundingTooltipId(null);
                setFundingPopupPosition(null);
            }
            if (openRateChangeTooltipId) {
                setOpenRateChangeTooltipId(null);
                setRateChangePopupPosition(null);
            }
        };
        
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [openTooltipId, openMaturityTooltipId, openFundingTooltipId, openRateChangeTooltipId]);

    /**
     * Calculate tooltip position relative to viewport
     * @param {DOMRect} rect - Element bounding rect
     * @param {number} tooltipWidth - Width of tooltip
     * @returns {TooltipPosition}
     */
    const calculateTooltipPosition = useCallback((/** @type {DOMRect} */ rect, /** @type {number} */ tooltipWidth = 400) => {
        const top = rect.bottom + 8;
        const left = rect.left - 16;
        const wouldOverflowRight = left + tooltipWidth > window.innerWidth;
        
        // Calculate arrow position to point to the center of the clicked element
        const elementCenterX = rect.left + (rect.width / 2);
        
        if (wouldOverflowRight) {
            // Tooltip positioned from right side
            const tooltipRight = window.innerWidth - rect.right + 16;
            const tooltipLeft = window.innerWidth - tooltipRight - tooltipWidth;
            const arrowLeft = elementCenterX - tooltipLeft - 16; // 16 is half the arrow width
            
            return {
                top,
                right: tooltipRight,
                width: tooltipWidth,
                arrowLeft: Math.max(16, Math.min(arrowLeft, tooltipWidth - 48)) // Constrain arrow within tooltip
            };
        } else {
            const arrowLeft = elementCenterX - left - 16; // 16 is half the arrow width
            
            return {
                top,
                left,
                width: tooltipWidth,
                arrowLeft: Math.max(16, Math.min(arrowLeft, tooltipWidth - 48)) // Constrain arrow within tooltip
            };
        }
    }, []);

    const handleInfoIconClick = useCallback((/** @type {React.MouseEvent} */ event, /** @type {string} */ accountId) => {
        event.stopPropagation();
        
        if (openTooltipId === accountId) {
            setOpenTooltipId(null);
            setPopupPosition(null);
        } else {
            const rect = event.currentTarget.getBoundingClientRect();
            setPopupPosition(calculateTooltipPosition(rect));
            setOpenTooltipId(accountId);
        }
    }, [openTooltipId, calculateTooltipPosition]);

    const handleMaturityInfoIconClick = useCallback((/** @type {React.MouseEvent} */ event, /** @type {string} */ accountId) => {
        event.stopPropagation();
        
        if (openMaturityTooltipId === accountId) {
            setOpenMaturityTooltipId(null);
            setMaturityPopupPosition(null);
        } else {
            const textElement = event.currentTarget;
            const infoRow = textElement.closest('.account-card__info-row');
            // Use the green info icon (info-icon-inline) for positioning
            const greenInfoIcon = infoRow ? infoRow.querySelector('.info-icon-inline') : null;
            
            const targetElement = greenInfoIcon || textElement;
            const rect = targetElement.getBoundingClientRect();
            
            setMaturityPopupPosition(calculateTooltipPosition(rect));
            setOpenMaturityTooltipId(accountId);
        }
    }, [openMaturityTooltipId, calculateTooltipPosition]);

    const handleAvailableBalanceIconClick = useCallback((/** @type {React.MouseEvent} */ event, /** @type {string} */ accountId) => {
        event.stopPropagation();
        
        const tooltipId = `${accountId}-available`;
        
        if (openTooltipId === tooltipId) {
            setOpenTooltipId(null);
            setPopupPosition(null);
        } else {
            const rect = event.currentTarget.getBoundingClientRect();
            setPopupPosition(calculateTooltipPosition(rect));
            setOpenTooltipId(tooltipId);
        }
    }, [openTooltipId, calculateTooltipPosition]);

    const handleFundingInfoIconClick = useCallback((/** @type {React.MouseEvent} */ event, /** @type {string} */ accountId) => {
        event.preventDefault();
        event.stopPropagation();
        
        if (openFundingTooltipId === accountId) {
            setOpenFundingTooltipId(null);
            setFundingPopupPosition(null);
        } else {
            const textElement = event.currentTarget;
            const infoRow = textElement.closest('.account-card__info-row');
            // Use the green info icon (info-icon-inline) for positioning
            const greenInfoIcon = infoRow ? infoRow.querySelector('.info-icon-inline') : null;
            
            const targetElement = greenInfoIcon || textElement;
            const rect = targetElement.getBoundingClientRect();
            
            setFundingPopupPosition(calculateTooltipPosition(rect));
            setOpenFundingTooltipId(accountId);
        }
    }, [openFundingTooltipId, calculateTooltipPosition]);

    const handleRateChangeInfoIconClick = useCallback((/** @type {React.MouseEvent} */ event, /** @type {string} */ accountId) => {
        event.preventDefault();
        event.stopPropagation();
        
        if (openRateChangeTooltipId === accountId) {
            setOpenRateChangeTooltipId(null);
            setRateChangePopupPosition(null);
        } else {
            const textElement = event.currentTarget;
            // For rate change, use the "View changes" link as the target for arrow positioning
            const viewChangesLink = textElement.querySelector('.view-changes-link');
            
            const targetElement = viewChangesLink || textElement;
            const rect = targetElement.getBoundingClientRect();
            
            setRateChangePopupPosition(calculateTooltipPosition(rect));
            setOpenRateChangeTooltipId(accountId);
        }
    }, [openRateChangeTooltipId, calculateTooltipPosition]);

    const toggleIsaTooltip = useCallback(() => {
        setShowIsaTooltip(prev => !prev);
    }, []);

    return {
        openTooltipId,
        popupPosition,
        openMaturityTooltipId,
        maturityPopupPosition,
        openFundingTooltipId,
        fundingPopupPosition,
        openRateChangeTooltipId,
        rateChangePopupPosition,
        showIsaTooltip,
        handleInfoIconClick,
        handleMaturityInfoIconClick,
        handleAvailableBalanceIconClick,
        handleFundingInfoIconClick,
        handleRateChangeInfoIconClick,
        toggleIsaTooltip
    };
}
